/**
 * UUID v4 generator that also works outside a secure context.
 *
 * `crypto.randomUUID` is secure-context-only, so it is undefined whenever the app
 * is served over plain http — which is exactly how the E2E harness serves it. The
 * result was silent: every letter click threw before its SelectLetter was sent, the
 * server's auto-fill finished the round anyway, and the bot-game specs stayed green
 * without ever exercising a real click. Production is https and unaffected, but the
 * app should not depend on the transport for an id it only needs to be unique.
 *
 * `crypto.getRandomValues` carries no such restriction, so the fallback is still
 * cryptographically random rather than Math.random.
 */
export function randomUuid(): string {
  if (typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  const bytes = crypto.getRandomValues(new Uint8Array(16));
  // Pin the version (4) and variant (RFC 4122) bits.
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;

  const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
